import React from "react";

/**
 * AdminPanelErrorBoundary — wraps a single admin panel so a render or
 * fetch failure in ONE panel can't take the whole console down.
 *
 * Before this, an unhandled error in any of the 15+ panels mounted in
 * AdminConsolePage caused the parent JSX tree to unmount silently —
 * Eric's "admin dashboard glitchy / can't open the whole thing"
 * (Jun 16, 2026). React error boundaries are the only way to stop the
 * blast radius at a render error since async errors in effects do
 * bubble to the root unless caught locally.
 *
 * Renders a small "Panel unavailable" tile so the admin can still see
 * the OTHER panels and use them. Click the panel name to reload the
 * page and try again.
 */
class AdminPanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    // Logging is the only observability we have post-deploy; we don't
    // ship Sentry by default. Keep it simple — console.error is fine.
    // eslint-disable-next-line no-console
    console.error(`[AdminPanel:${this.props.name || "?"}] crashed:`, err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="bg-red-950/40 border border-red-800/60 rounded-lg p-4 text-sm"
          data-testid={`admin-panel-error-${(this.props.name || "panel").toLowerCase()}`}
        >
          <div className="text-red-200 font-medium mb-1">
            {this.props.name || "Panel"} is unavailable
          </div>
          <div className="text-red-300/80 text-xs">
            A render or fetch error in this panel was contained so the rest of
            the console stays usable. Open the browser dev console to see the
            error, or reload the page to retry.
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 text-[11px] text-red-200 underline hover:text-white"
          >
            Reload admin console
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AdminPanelErrorBoundary;
